import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { Button, Form, Label, Modal } from "reactstrap";
import Uploadicon from "../../../../assets/images/pages/upload-cloud.png";
import CustomeButton from "../../Forms/CustomeButton/CustomeButton";
import { useDispatch } from "react-redux";
import { responseToaster } from "../../../../helperFunctions";
import { chargebackParse } from "../../../../redux/slices/customerDetailsSlice";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import ControlledDropDown from "../../Forms/Controller/ControlledDropDown";

const CustomerDetailsFileUpload = (props) => {
    const { isModalOpen, onCloseModal, onGet } = props;
    const dispatch = useDispatch();

    const [fileNotSupported, setFileNotSupported] = useState(null);
    const [mappings, setMappings] = useState({});
    const [availableHeadings, setAvailableHeadings] = useState([]);
    const [isButtonLoading, setIsButtonLoading] = useState(false);

    const schema = Yup.object({
        type: Yup.string().nullable().required("Please select type."),
        excelFile: Yup.string().required("Please upload excel file."),
        selectedHeadings: Yup.object().shape(
            fileNotSupported?.setRow?.reduce?.((acc, setRowItem) => {
                acc[setRowItem] = Yup.string().required(
                    `Please select a heading for ${setRowItem}`
                );
                return acc;
            }, {})
        ),
    });

    const prepareTypeOption = [
        { label: "Deposit", value: "Deposit" },
        { label: "Withdrawal", value: "Withdrawal" },
    ];

    const {
        control,
        setValue,
        getValues,
        trigger,
        handleSubmit,
        reset,
        watch,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            type: null,
            selectedHeadings: {},
        },
    });

    const onClose = () => {
        onCloseModal();
        reset();
        setMappings({});
        setAvailableHeadings([]);
        setFileNotSupported(null);
    };

    const onSubmit = async () => {
        const values = getValues();
        setIsButtonLoading(true);

        try {
            const fileUpload = new FormData();

            if (values?.excelFile) {
                fileUpload.append("excelFile", values?.excelFile);
            }

            fileUpload.append("type", values?.type);

            Object.entries(mappings)?.forEach(([key, value]) => {
                fileUpload.append(`header_mappings[${key}]`, value);
                fileUpload.append(`header[]`, value);
            });

            const res = await dispatch(chargebackParse(fileUpload)).unwrap();

            if (res?.message === "File data not supported") {
                setFileNotSupported(res);
                setAvailableHeadings(res?.heading);
            } else {
                responseToaster(res);
                onClose();
                await onGet();
            }

            setIsButtonLoading(false);
        } catch (err) {
            setIsButtonLoading(false);
        }
    };

    const handleMappingChange = (setRowItem, selectedHeading) => {
        setMappings((prev) => ({
            ...prev,
            [setRowItem]: selectedHeading,
        }));
        setValue(`selectedHeadings.${setRowItem}`, selectedHeading);
        trigger(`selectedHeadings.${setRowItem}`);
    };

    const disabledOptions = Object.values(mappings) || [];

    return (
        <Modal isOpen={isModalOpen} toggle={onClose} centered>
            <Form onSubmit={handleSubmit(onSubmit)}>
                <div className="modal-header">
                    <h3 className="modal-title mt-0">Customer Details</h3>
                    <i onClick={onClose} className="fa-solid fa-xmark"></i>
                </div>

                <div className="modal-body">
                    {/* Type Dropdown */}
                    <ControlledDropDown
                        name="type"
                        label="Type"
                        placeholder="Select Type"
                        options={prepareTypeOption}
                        control={control}
                        errors={errors}
                    />

                    {/* File Upload */}
                    <div className="select-div mt-1">
                        <Label>Upload File</Label>
                        <div className="proof-sec text-start">
                            <label className="label">
                                <input
                                    id="excelFile"
                                    name="excelFile"
                                    type="file"
                                    accept=".xlsx, .xls, .csv"
                                    onChange={(e) => {
                                        setValue("excelFile", e?.target?.files[0]);
                                        trigger("excelFile");
                                    }}
                                />
                                <p>
                                    <img src={Uploadicon} className="me-1 img-fluid" />
                                    <span
                                        className={
                                            watch("excelFile")?.name ? "themecolor" : ""
                                        }
                                    >
                                        {watch("excelFile")?.name || "Upload File (xlsx, xls, csv)"}
                                    </span>
                                </p>
                            </label>

                            {errors?.excelFile?.message && (
                                <span style={{ color: "red", fontSize: 12 }}>
                                    {errors?.excelFile?.message}
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Header Mapping */}
                    {fileNotSupported && (
                        <div className="pt-50">
                            {fileNotSupported?.setRow?.map((setRowItem) => (
                                <div key={setRowItem} className="row mb-25">
                                    <div className="col-3">
                                        <Label style={{ paddingTop: "6px" }}>
                                            {setRowItem}
                                        </Label>
                                    </div>
                                    <div className="col-9">
                                        <ControlledDropDown
                                            name={`selectedHeading-${setRowItem}`}
                                            isDefaultOption={false}
                                            placeholder={`Select ${setRowItem}`}
                                            value={mappings?.[setRowItem]}
                                            menuPlacement="auto"
                                            options={availableHeadings?.map(
                                                (heading) => ({
                                                    value: heading,
                                                    label: heading,
                                                    disabled:
                                                        disabledOptions.includes(heading),
                                                })
                                            )}
                                            onChange={(e) =>
                                                handleMappingChange(
                                                    setRowItem,
                                                    e.value
                                                )
                                            }
                                            control={control}
                                        />
                                        {errors?.selectedHeadings?.[setRowItem] && (
                                            <span
                                                style={{
                                                    color: "red",
                                                    fontSize: 12,
                                                }}
                                            >
                                                {
                                                    errors?.selectedHeadings?.[
                                                        setRowItem
                                                    ]?.message
                                                }
                                            </span>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="modal-footer">
                    <div className="d-flex justify-content-end button-space">
                        <Button
                            type="button"
                            className="graybutton"
                            onClick={onClose}
                        >
                            Cancel
                        </Button>
                        <CustomeButton
                            type="submit"
                            isButtonLoading={isButtonLoading}
                            className="submitbutton"
                        >
                            Submit
                        </CustomeButton>
                    </div>
                </div>
            </Form>
        </Modal>
    );
};

export default CustomerDetailsFileUpload;
